/* eslint-disable eag/match-named-export */
import {ThemeColorPath} from '../types/ThemePaths';

/**
 * Converts ThemeColorPath to Tailwind bg-* class
 * Uses CSS variables defined in theme.css
 */
export const BG_COLOR_CLASSES: Record<ThemeColorPath, string> = {
  // ==========================================================================
  // SIMPLIFIED SEMANTIC COLORS (preferred for new code)
  // ==========================================================================
  primary: 'bg-[var(--color-primary)]',
  secondary: 'bg-[var(--color-secondary)]',
  tertiary: 'bg-[var(--color-tertiary)]',
  informational: 'bg-[var(--color-informational)]',
  success: 'bg-[var(--color-success)]',
  warning: 'bg-[var(--color-warning)]',
  danger: 'bg-[var(--color-danger)]',
  link: 'bg-[var(--color-link)]',
  // Simplified general colors
  accent: 'bg-[var(--color-accent)]',
  separator: 'bg-[var(--color-separator)]',
  white: 'bg-[var(--color-white)]',
  black: 'bg-[var(--color-black)]',
  transparent: 'bg-transparent',
  // ==========================================================================
  // LEGACY PREFIXED PATHS (backward compatible with DMS/existing code)
  // These map to the same CSS variables as the simplified versions
  // ==========================================================================
  // Legacy text.* paths
  'text.primary': 'bg-[var(--color-primary)]',
  'text.secondary': 'bg-[var(--color-secondary)]',
  'text.tertiary': 'bg-[var(--color-tertiary)]',
  'text.informational': 'bg-[var(--color-informational)]',
  'text.success': 'bg-[var(--color-success)]',
  'text.warning': 'bg-[var(--color-warning)]',
  'text.danger': 'bg-[var(--color-danger)]',
  'text.link': 'bg-[var(--color-link)]',
  'text.white': 'bg-[var(--color-white)]',
  // Legacy general.* paths
  'general.accent': 'bg-[var(--color-accent)]',
  'general.separator': 'bg-[var(--color-separator)]',
  'general.white': 'bg-[var(--color-white)]',
  'general.black': 'bg-[var(--color-black)]',
  'general.transparent': 'bg-transparent',
  // ==========================================================================
  // SEVERITY COLORS
  // ==========================================================================
  'severity.informational': 'bg-[var(--color-severity-informational)]',
  'severity.warning': 'bg-[var(--color-severity-warning)]',
  'severity.success': 'bg-[var(--color-severity-success)]',
  'severity.danger': 'bg-[var(--color-severity-danger)]',
  'severity.informational.hover': 'bg-[var(--color-severity-informational-hover)]',
  'severity.warning.hover': 'bg-[var(--color-severity-warning-hover)]',
  'severity.success.hover': 'bg-[var(--color-severity-success-hover)]',
  'severity.danger.hover': 'bg-[var(--color-severity-danger-hover)]',
  'severity.informational.active': 'bg-[var(--color-severity-informational-active)]',
  'severity.warning.active': 'bg-[var(--color-severity-warning-active)]',
  'severity.success.active': 'bg-[var(--color-severity-success-active)]',
  'severity.danger.active': 'bg-[var(--color-severity-danger-active)]',
  // Palette colors - Black
  'palettes.black.10.100': 'bg-[var(--color-palettes-black-10-100)]',
  'palettes.black.900.70': 'bg-[var(--color-palettes-black-900-70)]',
  'palettes.black.900.60': 'bg-[var(--color-palettes-black-900-60)]',
  'palettes.black.900.40': 'bg-[var(--color-palettes-black-900-40)]',
  'palettes.black.900.20': 'bg-[var(--color-palettes-black-900-20)]',
  // Palette colors - White
  'palettes.white.10.100': 'bg-[var(--color-palettes-white-10-100)]',
  'palettes.white.10.80': 'bg-[var(--color-palettes-white-10-80)]',
  'palettes.white.10.40': 'bg-[var(--color-palettes-white-10-40)]',
  'palettes.white.10.20': 'bg-[var(--color-palettes-white-10-20)]',
  // Palette colors - Neutral
  'palettes.neutral.10.100': 'bg-[var(--color-palettes-neutral-10-100)]',
  'palettes.neutral.10.40': 'bg-[var(--color-palettes-neutral-10-40)]',
  'palettes.neutral.20.100': 'bg-[var(--color-palettes-neutral-20-100)]',
  'palettes.neutral.30.100': 'bg-[var(--color-palettes-neutral-30-100)]',
  'palettes.neutral.40.100': 'bg-[var(--color-palettes-neutral-40-100)]',
  'palettes.neutral.40.40': 'bg-[var(--color-palettes-neutral-40-40)]',
  'palettes.neutral.50.100': 'bg-[var(--color-palettes-neutral-50-100)]',
  'palettes.neutral.60.100': 'bg-[var(--color-palettes-neutral-60-100)]',
  'palettes.neutral.60.40': 'bg-[var(--color-palettes-neutral-60-40)]',
  'palettes.neutral.70.100': 'bg-[var(--color-palettes-neutral-70-100)]',
  'palettes.neutral.70.40': 'bg-[var(--color-palettes-neutral-70-40)]',
  'palettes.neutral.80.100': 'bg-[var(--color-palettes-neutral-80-100)]',
  'palettes.neutral.80.40': 'bg-[var(--color-palettes-neutral-80-40)]',
  'palettes.neutral.90.100': 'bg-[var(--color-palettes-neutral-90-100)]',
  'palettes.neutral.100.100': 'bg-[var(--color-palettes-neutral-100-100)]',
  'palettes.neutral.200.100': 'bg-[var(--color-palettes-neutral-200-100)]',
  'palettes.neutral.300.100': 'bg-[var(--color-palettes-neutral-300-100)]',
  'palettes.neutral.400.100': 'bg-[var(--color-palettes-neutral-400-100)]',
  'palettes.neutral.500.100': 'bg-[var(--color-palettes-neutral-500-100)]',
  'palettes.neutral.600.100': 'bg-[var(--color-palettes-neutral-600-100)]',
  'palettes.neutral.700.100': 'bg-[var(--color-palettes-neutral-700-100)]',
  'palettes.neutral.800.100': 'bg-[var(--color-palettes-neutral-800-100)]',
  'palettes.neutral.900.100': 'bg-[var(--color-palettes-neutral-900-100)]',
  'palettes.neutral.900.40': 'bg-[var(--color-palettes-neutral-900-40)]',
  'palettes.neutral.900.20': 'bg-[var(--color-palettes-neutral-900-20)]',
  // Palette colors - Blue
  'palettes.blue.10.100': 'bg-[var(--color-palettes-blue-10-100)]',
  'palettes.blue.20.100': 'bg-[var(--color-palettes-blue-20-100)]',
  'palettes.blue.30.100': 'bg-[var(--color-palettes-blue-30-100)]',
  'palettes.blue.40.100': 'bg-[var(--color-palettes-blue-40-100)]',
  'palettes.blue.50.100': 'bg-[var(--color-palettes-blue-50-100)]',
  'palettes.blue.60.100': 'bg-[var(--color-palettes-blue-60-100)]',
  'palettes.blue.70.100': 'bg-[var(--color-palettes-blue-70-100)]',
  'palettes.blue.80.100': 'bg-[var(--color-palettes-blue-80-100)]',
  'palettes.blue.90.100': 'bg-[var(--color-palettes-blue-90-100)]',
  // Palette colors - Red
  'palettes.red.10.100': 'bg-[var(--color-palettes-red-10-100)]',
  'palettes.red.20.100': 'bg-[var(--color-palettes-red-20-100)]',
  'palettes.red.30.100': 'bg-[var(--color-palettes-red-30-100)]',
  'palettes.red.40.100': 'bg-[var(--color-palettes-red-40-100)]',
  'palettes.red.50.100': 'bg-[var(--color-palettes-red-50-100)]',
  'palettes.red.60.100': 'bg-[var(--color-palettes-red-60-100)]',
  'palettes.red.70.100': 'bg-[var(--color-palettes-red-70-100)]',
  'palettes.red.80.100': 'bg-[var(--color-palettes-red-80-100)]',
  'palettes.red.90.100': 'bg-[var(--color-palettes-red-90-100)]',
  // Palette colors - Magenta
  'palettes.magenta.10.100': 'bg-[var(--color-palettes-magenta-10-100)]',
  'palettes.magenta.20.100': 'bg-[var(--color-palettes-magenta-20-100)]',
  'palettes.magenta.30.100': 'bg-[var(--color-palettes-magenta-30-100)]',
  'palettes.magenta.40.100': 'bg-[var(--color-palettes-magenta-40-100)]',
  'palettes.magenta.50.100': 'bg-[var(--color-palettes-magenta-50-100)]',
  'palettes.magenta.60.100': 'bg-[var(--color-palettes-magenta-60-100)]',
  'palettes.magenta.70.100': 'bg-[var(--color-palettes-magenta-70-100)]',
  'palettes.magenta.80.100': 'bg-[var(--color-palettes-magenta-80-100)]',
  'palettes.magenta.90.100': 'bg-[var(--color-palettes-magenta-90-100)]',
  // Palette colors - Purple
  'palettes.purple.10.100': 'bg-[var(--color-palettes-purple-10-100)]',
  'palettes.purple.20.100': 'bg-[var(--color-palettes-purple-20-100)]',
  'palettes.purple.30.100': 'bg-[var(--color-palettes-purple-30-100)]',
  'palettes.purple.40.100': 'bg-[var(--color-palettes-purple-40-100)]',
  'palettes.purple.50.100': 'bg-[var(--color-palettes-purple-50-100)]',
  'palettes.purple.60.100': 'bg-[var(--color-palettes-purple-60-100)]',
  'palettes.purple.70.100': 'bg-[var(--color-palettes-purple-70-100)]',
  'palettes.purple.80.100': 'bg-[var(--color-palettes-purple-80-100)]',
  'palettes.purple.90.100': 'bg-[var(--color-palettes-purple-90-100)]',
  // Palette colors - Teal
  'palettes.teal.10.100': 'bg-[var(--color-palettes-teal-10-100)]',
  'palettes.teal.20.100': 'bg-[var(--color-palettes-teal-20-100)]',
  'palettes.teal.30.100': 'bg-[var(--color-palettes-teal-30-100)]',
  'palettes.teal.40.100': 'bg-[var(--color-palettes-teal-40-100)]',
  'palettes.teal.50.100': 'bg-[var(--color-palettes-teal-50-100)]',
  'palettes.teal.60.100': 'bg-[var(--color-palettes-teal-60-100)]',
  'palettes.teal.70.100': 'bg-[var(--color-palettes-teal-70-100)]',
  'palettes.teal.80.100': 'bg-[var(--color-palettes-teal-80-100)]',
  'palettes.teal.90.100': 'bg-[var(--color-palettes-teal-90-100)]',
  // Palette colors - Green
  'palettes.green.10.100': 'bg-[var(--color-palettes-green-10-100)]',
  'palettes.green.20.100': 'bg-[var(--color-palettes-green-20-100)]',
  'palettes.green.30.100': 'bg-[var(--color-palettes-green-30-100)]',
  'palettes.green.40.100': 'bg-[var(--color-palettes-green-40-100)]',
  'palettes.green.50.100': 'bg-[var(--color-palettes-green-50-100)]',
  'palettes.green.60.100': 'bg-[var(--color-palettes-green-60-100)]',
  'palettes.green.70.100': 'bg-[var(--color-palettes-green-70-100)]',
  'palettes.green.80.100': 'bg-[var(--color-palettes-green-80-100)]',
  'palettes.green.90.100': 'bg-[var(--color-palettes-green-90-100)]',
  // Palette colors - Yellow
  'palettes.yellow.10.100': 'bg-[var(--color-palettes-yellow-10-100)]',
  'palettes.yellow.20.100': 'bg-[var(--color-palettes-yellow-20-100)]',
  'palettes.yellow.30.100': 'bg-[var(--color-palettes-yellow-30-100)]',
  'palettes.yellow.40.100': 'bg-[var(--color-palettes-yellow-40-100)]',
  'palettes.yellow.50.100': 'bg-[var(--color-palettes-yellow-50-100)]',
  'palettes.yellow.60.100': 'bg-[var(--color-palettes-yellow-60-100)]',
  'palettes.yellow.70.100': 'bg-[var(--color-palettes-yellow-70-100)]',
  'palettes.yellow.80.100': 'bg-[var(--color-palettes-yellow-80-100)]',
  'palettes.yellow.90.100': 'bg-[var(--color-palettes-yellow-90-100)]',
  // Palette colors - Orange
  'palettes.orange.10.100': 'bg-[var(--color-palettes-orange-10-100)]',
  'palettes.orange.20.100': 'bg-[var(--color-palettes-orange-20-100)]',
  'palettes.orange.30.100': 'bg-[var(--color-palettes-orange-30-100)]',
  'palettes.orange.40.100': 'bg-[var(--color-palettes-orange-40-100)]',
  'palettes.orange.50.100': 'bg-[var(--color-palettes-orange-50-100)]',
  'palettes.orange.60.100': 'bg-[var(--color-palettes-orange-60-100)]',
  'palettes.orange.70.100': 'bg-[var(--color-palettes-orange-70-100)]',
  'palettes.orange.80.100': 'bg-[var(--color-palettes-orange-80-100)]',
  'palettes.orange.90.100': 'bg-[var(--color-palettes-orange-90-100)]',
};
